
import { Injectable, Injector } from '@angular/core';
import {
  HttpInterceptor,
  HttpRequest,
  HttpResponse,
  HttpHandler,
  HttpEvent,
  HttpErrorResponse,
  HttpClient, HttpHeaders, HttpParams, HttpParameterCodec
} from '@angular/common/http';

import { Observable, Subject } from 'rxjs';
import { map, catchError, mergeMap, switchMap } from 'rxjs/operators';
import { _throw } from 'rxjs/observable/throw';
import { AuthService } from './auth.service';
import { ConfigurationService } from './configuration.service';


// Angular's default HttpUrlEncodingCodec un-does encodeURIComponent's escaping of '+', '/'
// and '=' for readability, but a literal (unescaped) '+' in an application/x-www-form-urlencoded
// body decodes server-side as a space - silently corrupting any base64 value (password grant
// credentials, refresh tokens, MFA tickets, trusted-device tokens) that happens to contain one.
// This codec encodes values properly and leaves them properly encoded.
class FormUrlEncodedCodec implements HttpParameterCodec {
  encodeKey(key: string): string { return encodeURIComponent(key); }
  encodeValue(value: string): string { return encodeURIComponent(value); }
  decodeKey(key: string): string { return decodeURIComponent(key); }
  decodeValue(value: string): string { return decodeURIComponent(value); }
}


@Injectable()
export class EndpointFactory {
  static readonly apiVersion: string = "1";

  private readonly _loginUrl: string = "/connect/token";
  private readonly _trustDeviceUrl: string = "/api/account/trustDevice";
  private readonly _forgetDeviceUrl: string = "/api/account/forgetDevice";

  private get loginUrl() { return this.configurations.apiAddress + this._loginUrl; }
  private get trustDeviceUrl() { return this.configurations.apiAddress + this._trustDeviceUrl; }
  private get forgetDeviceUrl() { return this.configurations.apiAddress + this._forgetDeviceUrl; }

  private taskPauser: Subject<any>;
  private isRefreshingLogin: boolean;

  private _authService: AuthService;

  private get authService() {
    if (!this._authService)
      this._authService = this.injector.get(AuthService);

    return this._authService;
  }



  constructor(protected http: HttpClient, protected configurations: ConfigurationService, private injector: Injector) {

  }


  getLoginEndpoint<T>(userName: string, password: string, trustedDeviceToken?: string): Observable<T> {

    let header = new HttpHeaders({ 'Content-Type': 'application/x-www-form-urlencoded' });

    let params = new HttpParams({ encoder: new FormUrlEncodedCodec() })
      .append('username', userName)
      .append('password', password)
      .append('grant_type', 'password')
      .append('scope', 'openid email phone profile offline_access roles');

    if (trustedDeviceToken) {
      params = params.append('trusted_device_token', trustedDeviceToken);
    }

    let requestBody = params.toString();

    return this.http.post<T>(this.loginUrl +'?isWeb=true', requestBody, { headers: header });
  }


  getTrustDeviceEndpoint<T>(): Observable<T> {
    return this.http.post<T>(this.trustDeviceUrl, null, this.getRequestHeaders());
  }


  getForgetDeviceEndpoint<T>(trustedDeviceToken: string): Observable<T> {
    return this.http.post<T>(this.forgetDeviceUrl, JSON.stringify({ trustedDeviceToken }), this.getRequestHeaders());
  }


  getVerifyMfaEndpoint<T>(mfaTicket: string, code: string): Observable<T> {

    let header = new HttpHeaders({ 'Content-Type': 'application/x-www-form-urlencoded' });

    let params = new HttpParams({ encoder: new FormUrlEncodedCodec() })
      .append('mfa_ticket', mfaTicket)
      .append('code', code)
      .append('grant_type', 'mfa_code');

    let requestBody = params.toString();

    return this.http.post<T>(this.loginUrl + '?isWeb=true', requestBody, { headers: header });
  }


  getCompletePasswordChangeEndpoint<T>(changeTicket: string, newPassword: string): Observable<T> {

    let header = new HttpHeaders({ 'Content-Type': 'application/x-www-form-urlencoded' });

    let params = new HttpParams({ encoder: new FormUrlEncodedCodec() })
      .append('change_ticket', changeTicket)
      .append('new_password', newPassword)
      .append('grant_type', 'password_change');

    let requestBody = params.toString();

    return this.http.post<T>(this.loginUrl + '?isWeb=true', requestBody, { headers: header });
  }


  getRefreshLoginEndpoint<T>(): Observable<T> {

    let header = new HttpHeaders({ 'Content-Type': 'application/x-www-form-urlencoded' });

    let params = new HttpParams({ encoder: new FormUrlEncodedCodec() })
      .append('refresh_token', this.authService.refreshToken)
      .append('grant_type', 'refresh_token')
      .append('scope', 'openid email phone profile offline_access roles');

    let requestBody = params.toString();

    return this.http.post<T>(this.loginUrl, requestBody, { headers: header }).pipe<T>(
      catchError(error => {
        return this.handleError(error, () => this.getRefreshLoginEndpoint());
      }));
  }



  protected getRequestHeaders(): { headers: HttpHeaders | { [header: string]: string | string[]; } } {
    let headers = new HttpHeaders({
      'Authorization': 'Bearer ' + this.authService.accessToken,
      'Content-Type': 'application/json',
      'Accept': `application/vnd.iman.v${EndpointFactory.apiVersion}+json, application/json, text/plain, */*`,
      'App-Version': ConfigurationService.appVersion
    });

    return { headers: headers };
  }



  protected handleError(error, continuation: () => Observable<any>) {

    if (error.status == 401) {
      if (this.isRefreshingLogin) {
        return this.pauseTask(continuation);
      }

      this.isRefreshingLogin = true;

      return this.authService.refreshLogin().pipe(
        mergeMap(data => {
          this.isRefreshingLogin = false;
          this.resumeTasks(true);

          return continuation();
        }),
        catchError(refreshLoginError => {
          this.isRefreshingLogin = false;
          this.resumeTasks(false);

          if (refreshLoginError.status == 401 || (refreshLoginError.url && refreshLoginError.url.toLowerCase().includes(this.loginUrl.toLowerCase()))) {
            this.authService.reLogin();
            return _throw('session expired');
          }
          else {
            return _throw(refreshLoginError || 'server error');
          }
        }));
    }

    if (error.url && error.url.toLowerCase().includes(this.loginUrl.toLowerCase())) {
      this.authService.reLogin();

      return _throw((error.error && error.error.error_description) ? `session expired (${error.error.error_description})` : 'session expired');
    }
    else {
      return _throw(error);
    }
  }



  private pauseTask(continuation: () => Observable<any>) {
    if (!this.taskPauser)
      this.taskPauser = new Subject();

    return this.taskPauser.pipe(switchMap(continueOp => {
      return continueOp ? continuation() : _throw('session expired');
    }));
  }


  private resumeTasks(continueOp: boolean) {
    setTimeout(() => {
      if (this.taskPauser) {
        this.taskPauser.next(continueOp);
        this.taskPauser.complete();
        this.taskPauser = null;
      }
    });
  }
}
